/**
 * HOW TALL IS ONE LINE OF TEXT, when the stylesheet never said.
 *
 * The member drawer reserves room on the right of its heading for the role
 * badge, but only when the name has gone to a second line. To know that, it
 * compares the heading's `scrollHeight` against the height of a single line.
 *
 * It used to read that height with `parseInt(getComputedStyle(el).lineHeight)`.
 * The heading declares no `line-height` at all, so the computed style answers
 * the keyword `normal` -- and `parseInt('normal')` is NaN, which loses every
 * comparison it takes part in. The measurement therefore said « one line » for
 * every name that has ever existed, and the rule written in the stylesheet
 * never applied once (DEV-278).
 *
 * The fix does not put a `line-height` in the stylesheet. It measures the way a
 * browser does when nothing is declared: a factor of the font size. A rule in
 * the stylesheet could be taken out again by somebody who had no idea it was
 * load-bearing; this cannot.
 */

/**
 * What browsers use for `normal`: the exact value is the font's own metrics, so
 * every engine lands somewhere near 1.2 and none of them promises it. The
 * tolerance below is what absorbs the difference -- we are asking « is this one
 * line or two », not « how tall exactly ».
 */
export const A_BROWSER_SPACES_NORMAL_LINES_BY = 1.2;

/**
 * Half a line, near enough. A second line adds a whole line's height, so any
 * threshold comfortably under one line separates the two cases, and this one
 * also swallows the gap between our 1.2 and the font's real spacing.
 */
export const MORE_THAN_ONE_LINE_STARTS_AT = 0.5;

type WhatTheStyleSays = Pick<CSSStyleDeclaration, 'lineHeight' | 'fontSize'>;

const A_PLAIN_NUMBER = /^\d*\.?\d+$/;

const positiveNumberIn = (value: string | undefined): number | null => {
  const parsed = Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * The height of one line, in pixels, or `null` when the style says nothing
 * usable at all -- a heading with no font size, which is a heading nobody can
 * measure. Callers treat `null` as « do not guess ».
 */
export const whatOneLineOfAHeadingMeasures = (style: WhatTheStyleSays): number | null => {
  const fontSize = positiveNumberIn(style.fontSize);
  const declared = (style.lineHeight ?? '').trim();

  // `normal` in a browser, the empty string in jsdom: both mean « nothing was
  // declared », and both are what sent the old measurement to NaN.
  if (declared.endsWith('px')) {
    const inPixels = positiveNumberIn(declared);
    if (inPixels !== null) return inPixels;
  } else if (declared.endsWith('%') && fontSize !== null) {
    const percentage = positiveNumberIn(declared);
    if (percentage !== null) return (percentage / 100) * fontSize;
  } else if (A_PLAIN_NUMBER.test(declared) && fontSize !== null) {
    // A unitless line-height multiplies the font size, as CSS defines it. The
    // test is for a PLAIN number, not for whatever parseFloat is willing to
    // read off the front of a string: `2em` would otherwise be taken as the
    // factor 2, and silently measure a line nobody asked for.
    const factor = positiveNumberIn(declared);
    if (factor !== null) return factor * fontSize;
  }

  return fontSize === null ? null : fontSize * A_BROWSER_SPACES_NORMAL_LINES_BY;
};

/**
 * Whether the heading's text has gone past a single line. `false` when the
 * style cannot be measured: an unmeasurable heading keeps the layout it has
 * rather than being given a margin on a guess.
 */
export const aHeadingTakesMoreThanOneLine = (style: WhatTheStyleSays, scrollHeight: number): boolean => {
  const oneLine = whatOneLineOfAHeadingMeasures(style);
  if (oneLine === null || !Number.isFinite(scrollHeight)) {
    return false;
  }

  return scrollHeight > oneLine * (1 + MORE_THAN_ONE_LINE_STARTS_AT);
};
